import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { fetchStudent as fetchStudentDoc } from '../../services/studentService';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { hasPerm, canViewBilling } from '../../utils/permissions';
import EmailComposer from '../../components/EmailFamilies/EmailComposer';
import { guardiansForStudent } from '../../components/EmailFamilies/recipients';
import StudentLessonsHistory from '../../components/StudentLessons/StudentLessonsHistory';
import StudentRepertoire from '../../components/Repertoire/StudentRepertoire';
import StudentMaterials from '../../components/StudentMaterials/StudentMaterials';
import PaymentHistory from '../../components/PaymentHistory/PaymentHistory';
import SendPortalInvite from '../../components/SendPortalInvite/SendPortalInvite';
import SendSlotChangeForm from '../../components/SendSlotChangeForm/SendSlotChangeForm';
import PortalInvites from '../../components/PortalInvites/PortalInvites';
import EditStudent from '../../components/EditStudent/EditStudent';
import StudentTimeSlots from '../../components/Scheduling/StudentTimeSlots';
import AssignTimeSlot from '../../components/Scheduling/AssignTimeSlot';
import { canScheduleStudent } from '../../utils/studentFilters';
import { getStudentTimeSlots } from '../../utils/schedulingUtils';
import { lessonDayFor, nextLessonYmdFor, legacyNextLessonDate } from '../../utils/studentSchedule';
import { useTestMode } from '../../contexts/TestModeContext';
import TestModeBanner from '../../components/TestMode/TestModeBanner';
import PortalHeader from '../../components/Portal/PortalHeader';
import '../../components/Portal/portalSurface.css';
import './StudentProfile.css';

const TABS = [
	// Overview first, and it is also the tab the page opens on (Anthony,
	// 2026-07-27) — land on context, then step across to Schedule.
	{ key: 'overview', label: 'Overview' },
	{ key: 'schedule', label: 'Schedule', icon: 'fa-calendar-clock' },
	{ key: 'lessons', label: 'Lessons & Attendance' },
	// Repertoire routed 2026-08-06 (C decision): the non-lesson Song Notes
	// surface — the component existed unrouted since N1b, with nothing able
	// to reach it.
	{ key: 'repertoire', label: 'Repertoire' },
	{ key: 'materials', label: 'Materials' },
	// `payments` is billing — appended only for admins/owners (see `tabs` below).
	// Teachers never see billing (portal-architecture §6); the payments read is
	// admin/owner-only and a swallowed denial otherwise renders as fake
	// "$0.00 / Paid in Full" (#21).
	{ key: 'payments', label: 'Payments & Balance', billing: true },
];

const dash = <span className="text-muted">—</span>;

const StudentProfile = () => {
	const { studentId } = useParams();
	const navigate = useNavigate();
	const { testMode } = useTestMode(); // eslint-disable-line no-unused-vars
	const { userProfile } = useAuth();
	const [student, setStudent] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	// Land on Overview (Anthony, 2026-07-27) — context first, then Schedule.
	// TABS now lists Overview first too, so order and default finally agree;
	// they disagreed before, and that contradiction is what sent the "student
	// page does not show the assigned Sunday slot" investigation to this tab
	// instead of to the Students roster, where the bug actually was.
	const [activeTab, setActiveTab] = useState('overview');
	const [showPortalInvite, setShowPortalInvite] = useState(false);
	const [showSlotChangeForm, setShowSlotChangeForm] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showAssignTimeSlot, setShowAssignTimeSlot] = useState(false);
	const [showEmailModal, setShowEmailModal] = useState(false);
	// The student's assigned slots, for the Overview "Lesson Day" row. The
	// Schedule tab's StudentTimeSlots fetches its own — this is a separate,
	// cheap read rather than lifting that component's state, so Overview stays
	// correct whether or not the Schedule tab has ever been opened.
	const [ownSlots, setOwnSlots] = useState([]);

	const canSendEmail = hasPerm(userProfile, 'sendEmail');
	const showBilling = canViewBilling(userProfile);
	const tabs = TABS.filter((t) => !t.billing || showBilling);

	const fetchStudent = async () => {
		try {
			const s = await fetchStudentDoc(studentId);
			if (s) {
				setStudent(s);
			} else {
				setError('Student not found');
			}
		} catch (err) {
			console.error('Error fetching student:', err);
			setError('Failed to load student data');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchStudent();
	}, [studentId]);

	useEffect(() => {
		let cancelled = false;
		getStudentTimeSlots(studentId)
			.then((s) => { if (!cancelled) setOwnSlots(s); })
			// Non-fatal: Overview falls back to the legacy field, exactly as before.
			.catch((err) => console.error('Error fetching student time slots:', err));
		return () => { cancelled = true; };
	}, [studentId]);

	const handleEditSuccess = () => {
		// Refresh student data after edit
		fetchStudent();
	};

	if (loading) {
		return (
			<>
				<PortalHeader title="Student Profile" />
				<div className="ps-page">
					<div className="ps-spinner" role="status" aria-label="Loading" />
				</div>
			</>
		);
	}

	if (error || !student) {
		return (
			<>
				<PortalHeader title="Student Profile" />
				<div className="ps-page">
					<div className="ps-alert ps-alert--danger">
						<strong>Error:</strong> {error || 'Student not found'}
					</div>
					<button className="ps-btn" onClick={() => navigate('/portal/work/students')}>
						<i className="fa-sharp fa-solid fa-arrow-left" /> Back to Students
					</button>
				</div>
			</>
		);
	}

	const studentName = student.fullName || `${student.firstName} ${student.lastName}`;

	// Canonical placement first, legacy `students.nextLesson` only as a fallback
	// — the same resolution the Students roster uses, from the same module, so
	// the two cannot disagree about a student's lesson day.
	const getLessonDay = () => lessonDayFor(ownSlots, student);

	// Next lesson, same precedence: the grid's soonest occurrence, legacy MMS
	// field only as a fallback. Parsed with legacyNextLessonDate rather than
	// `new Date(...)` — a date-only value goes through UTC midnight and renders
	// a day early in Central (#14). The roster already guarded that; this page
	// did not, so a legacy student could read one day off here and correct on
	// the roster for the same underlying value.
	const nextLessonAt = legacyNextLessonDate(
		nextLessonYmdFor(ownSlots) || student.nextLesson
	);

	return (
		<>
			<PortalHeader title="Student Profile" />
			<TestModeBanner />
			<div className="ps-page student-profile">
				<button
					className="ps-btn mb-3"
					onClick={() => navigate('/portal/work/students')}>
					<i className="fa-sharp fa-solid fa-arrow-left" /> Back to Students
				</button>

				{/* Header Card */}
				<div className="ps-card sp-header">
					<div className="sp-header__main">
						<h1 className="sp-header__name">{studentName}</h1>
						<div className="sp-badges">
							{/* Pronouns lead the row: they inform how staff address the
							    student, which matters before roster status does. Data has
							    existed on students since the 08-07 MMS backfill; this is
							    the first staff surface to show it. */}
							{student.pronouns && (
								<span className="ps-badge ps-badge--info">{student.pronouns}</span>
							)}
							<span className={`ps-badge ps-badge--${student.status === 'Active' ? 'success' : 'neutral'}`}>
								{student.status || 'Active'}
							</span>
							{student.age && <span className="ps-badge ps-badge--info">Age {student.age}</span>}
							{student.instrument && <span className="ps-badge ps-badge--neutral">{student.instrument}</span>}
						</div>
					</div>
					<div className="sp-header__actions">
						{/* Portal access goes out through createPortalClaim (claim-
						    link flow §3g), sendEmail-gated at the callable. Hide for
						    staff who'd only fail at send time (#22). Admin/owner
						    bypass via canSendEmail. */}
						{canSendEmail && (
							<button
								className="btn-cyv-teal sp-action-btn"
								onClick={() => setShowPortalInvite(true)}
								title="Send portal access invitation">
								<i className="fa-sharp fa-solid fa-paper-plane" /> Send Portal Access
							</button>
						)}
						{canSendEmail && (
							<button
								className="btn-cyv-ghost sp-action-btn"
								onClick={() => setShowEmailModal(true)}
								title="Email this family">
								<i className="fa-sharp fa-solid fa-envelope" /> Email this family
							</button>
						)}
						{/* Lesson-time change form (2026-09-16): emails the parent a
						    token link to the open weekly times; the pick comes back as
						    a registration request. Same send-email gate as the invite. */}
						{canSendEmail && (
							<button
								className="btn-cyv-ghost sp-action-btn"
								onClick={() => setShowSlotChangeForm(true)}
								title="Email the parent a form to pick a new weekly lesson time">
								<i className="fa-sharp fa-solid fa-calendar-clock" /> Send time-change form
							</button>
						)}
						<button
							className="btn-cyv-ghost sp-action-btn"
							onClick={() => setShowEditModal(true)}
							disabled={student.locked}
							title={student.locked ? 'Student is locked - unlock to edit' : 'Edit student information'}>
							<i className="fa-sharp fa-solid fa-pen-to-square" /> Edit
						</button>
					</div>
				</div>

				{/* Warning for locked/archived/test students */}
				{!canScheduleStudent(student) && (
					<div className="ps-alert ps-alert--warning">
						<i className="fa-sharp fa-solid fa-triangle-exclamation me-2" />
						<strong>Notice:</strong> This student is{' '}
						{student.locked && 'locked'}
						{student.archived && 'archived'}
						{student.isTestStudent && 'a test student'}.{' '}
						{student.locked && 'Locked students are protected from write operations during development.'}
						{student.archived && 'Archived students should not be scheduled for new lessons or sent portal invites.'}
						{student.isTestStudent && 'Test students are for development purposes only.'}
					</div>
				)}

				{/* Tabs */}
				<div className="ps-tabs" role="tablist">
					{tabs.map((t) => (
						<button
							key={t.key}
							type="button"
							role="tab"
							aria-selected={activeTab === t.key}
							className={`ps-tab${activeTab === t.key ? ' is-active' : ''}`}
							onClick={() => setActiveTab(t.key)}>
							{t.icon && <i className={`fa-sharp fa-solid ${t.icon}`} />}
							{t.label}
						</button>
					))}
				</div>

				{activeTab === 'schedule' && (
					<StudentTimeSlots
						studentId={studentId}
						onAssignClick={() => setShowAssignTimeSlot(true)}
					/>
				)}

				{activeTab === 'repertoire' && (
					<StudentRepertoire studentId={studentId} studentName={student.fullName} />
				)}

				{activeTab === 'overview' && (
					<div className="sp-grid">
						{/* Contact Information */}
						<div className="ps-card">
							<h2 className="ps-card__band">Contact Information</h2>
							<dl className="ps-deflist">
								<dt>Email</dt><dd>{student.email || dash}</dd>
								<dt>Phone</dt><dd>{student.phone || dash}</dd>
								<dt>Birthday</dt><dd>{student.birthday || dash}</dd>
								<dt>School</dt><dd>{student.school || dash}</dd>
								<dt>Address</dt><dd>{student.parent1?.address || dash}</dd>
							</dl>
						</div>

						{/* Parent/Guardian Information */}
						<div className="ps-card">
							<h2 className="ps-card__band">Parent / Guardian Contact</h2>
							{student.parent1?.firstName && (
								<>
									<h6 className="sp-subhead">Primary contact</h6>
									<dl className="ps-deflist">
										<dt>Name</dt><dd>{student.parent1.firstName} {student.parent1.lastName}</dd>
										<dt>Email</dt><dd>{student.parent1.email || dash}</dd>
										<dt>Phone</dt><dd>{student.parent1.phone || dash}</dd>
									</dl>
								</>
							)}
							{student.parent2?.firstName && (
								<>
									<h6 className="sp-subhead mt-3">Secondary contact</h6>
									<dl className="ps-deflist">
										<dt>Name</dt><dd>{student.parent2.firstName} {student.parent2.lastName}</dd>
										<dt>Email</dt><dd>{student.parent2.email || dash}</dd>
										<dt>Phone</dt><dd>{student.parent2.phone || dash}</dd>
									</dl>
								</>
							)}
							{!student.parent1?.firstName && (
								<p className="text-muted mb-0">No parent contact information available</p>
							)}
						</div>

						{/* Lesson Information */}
						<div className="ps-card">
							<h2 className="ps-card__band">Lesson Information</h2>
							<dl className="ps-deflist">
								<dt>Lesson Day</dt><dd>{getLessonDay() || dash}</dd>
								<dt>Last Lesson</dt><dd>{student.lastLesson ? new Date(student.lastLesson).toLocaleDateString() : dash}</dd>
								<dt>Next Lesson</dt><dd>{nextLessonAt ? nextLessonAt.toLocaleDateString() : dash}</dd>
								<dt>Lesson Length</dt><dd>{student.defaultLessonLength || 45} minutes</dd>
								<dt>Date Started</dt><dd>{student.dateStarted || dash}</dd>
							</dl>
						</div>

						{/* Portal Access — who was invited, whether they claimed it,
						    and the revoke valve. Read is staff-wide; `canManage`
						    mirrors the callable's own sendEmail gate so the button
						    only appears for staff whose revoke would succeed. */}
						<PortalInvites
							studentId={studentId}
							familyId={student.familyId}
							canManage={canSendEmail}
						/>

						{/* Billing Information */}
						<div className="ps-card">
							<h2 className="ps-card__band">Billing Information</h2>
							<dl className="ps-deflist">
								<dt>Billing Type</dt><dd>{student.billingType || dash}</dd>
								{/* TWO ROWS REMOVED 2026-08-09, both MMS-era fields that read as
								    authoritative next to live billing facts while deciding nothing.

								    "Makeup Credits" (students.makeupCredits): credits are a ledger
								    now — the makeupCredits COLLECTION plus students.makeupBalance.
								    Not repointed at makeupBalance yet on purpose: no staff credit
								    UI has landed, and the customer portal already renders the
								    balance families see.

								    "Price" (students.price): no billing code reads it — tuition
								    resolves from src/canon/season.json and is stamped onto the
								    invoice. Measured before removal: 60 of 64 students held the
								    identical "$220.00", last season's installment. A constant, and
								    a stale one.

								    Stored values are tombstoned, not deleted. */}
							</dl>
						</div>

						{/* Notes */}
						{student.notes && (
							<div className="ps-card sp-span-2">
								<h2 className="ps-card__band">Sign-Up Form &amp; Notes</h2>
								{(() => {
									// Parse the notes to extract Q&A pairs
									const lines = student.notes.split('\n');
									const sections = [];
									let currentSection = { title: '', items: [] };

									lines.forEach(line => {
										const trimmedLine = line.trim();

										if (trimmedLine.includes('signup form submission') ||
										    trimmedLine.includes('sign-up form submission')) {
											if (currentSection.items.length > 0) {
												sections.push(currentSection);
											}
											currentSection = { title: trimmedLine, items: [] };
										}
										else if (trimmedLine.includes(': ')) {
											const colonIndex = trimmedLine.indexOf(': ');
											const question = trimmedLine.substring(0, colonIndex);
											const answer = trimmedLine.substring(colonIndex + 2);
											if (answer && answer !== '(not provided)' && answer !== '(Not Provided)') {
												currentSection.items.push({ question, answer });
											}
										}
										else if (/^\d+\/\d+\/\d+/.test(trimmedLine)) {
											if (currentSection.items.length > 0) {
												sections.push(currentSection);
											}
											currentSection = { title: trimmedLine, items: [] };
										}
									});

									if (currentSection.items.length > 0) {
										sections.push(currentSection);
									}

									if (sections.length === 0) {
										return <p style={{ whiteSpace: 'pre-wrap' }}>{student.notes}</p>;
									}

									return sections.map((section, idx) => (
										<div key={idx} className="sp-note-section">
											{section.title && (
												<div className="sp-note-title">{section.title}</div>
											)}
											<div className="sp-note-items">
												{section.items.map((item, itemIdx) => (
													<div key={itemIdx} className="sp-note-item">
														<div className="sp-note-q">{item.question}</div>
														<div className="sp-note-a">{item.answer}</div>
													</div>
												))}
											</div>
										</div>
									));
								})()}
							</div>
						)}

						{/* Additional Info */}
						<div className="ps-card sp-span-2">
							<h2 className="ps-card__band">Additional Information</h2>
							<dl className="ps-deflist sp-deflist-2col">
								<dt>Referrer</dt><dd>{student.referrer || dash}</dd>
								<dt>Tags</dt><dd>{student.groupTags?.length > 0 ? student.groupTags.join(', ') : dash}</dd>
								<dt>MMS Student ID</dt><dd>{student.mmsStudentId || dash}</dd>
								<dt>Imported</dt><dd>{student.importedAt ? new Date(student.importedAt).toLocaleDateString() : dash}</dd>
							</dl>
						</div>
					</div>
				)}

				{activeTab === 'lessons' && (
					<StudentLessonsHistory
						studentId={studentId}
						studentName={studentName}
						isEmployeeView={true}
					/>
				)}

				{activeTab === 'materials' && (
					<StudentMaterials studentId={studentId} studentName={studentName} />
				)}

				{activeTab === 'payments' && showBilling && (
					<PaymentHistory
						studentId={studentId}
						studentName={studentName}
						isEmployeeView={true}
					/>
				)}

				{/* Shared Bootstrap modal components — deferred (separate surfaces) */}
				<SendPortalInvite
					show={showPortalInvite}
					onHide={() => setShowPortalInvite(false)}
					students={student ? [student] : []}
					onSuccess={(results) => {
						console.log('Portal invites sent:', results);
						setShowPortalInvite(false);
					}}
				/>
				<SendSlotChangeForm
					show={showSlotChangeForm}
					onHide={() => setShowSlotChangeForm(false)}
					student={student}
				/>
				<EditStudent
					show={showEditModal}
					onHide={() => setShowEditModal(false)}
					student={student}
					onSuccess={handleEditSuccess}
				/>
				<AssignTimeSlot
					show={showAssignTimeSlot}
					onHide={() => setShowAssignTimeSlot(false)}
					student={student}
					onSuccess={() => {
						setShowAssignTimeSlot(false);
					}}
				/>

				{/* E4 Layer 1 — "Email this family": same composer as the standalone
				    surface, pre-filled with this family's guardian chips. */}
				{canSendEmail && (
					<Modal
						show={showEmailModal}
						onHide={() => setShowEmailModal(false)}
						size="lg"
						centered>
						<Modal.Header closeButton>
							<Modal.Title>
								<i className="fa-sharp fa-solid fa-envelope me-2" />
								Email {studentName}'s family
							</Modal.Title>
						</Modal.Header>
						<Modal.Body>
							<EmailComposer
								initialRecipients={guardiansForStudent(student)}
								studentId={student.id}
							/>
						</Modal.Body>
					</Modal>
				)}
			</div>
		</>
	);
};

export default StudentProfile;
